import React, { ChangeEvent, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Modal,
  Button,
  Grid,
  Paper,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';

import { ContactListProps, Contact, BaseContactListState } from '@/types';
import { getIcon } from '@/components/common/icons/getIcon';
import AppModalDialog from '@/components/ui/AppModalDialog';

const ContactList: React.FC<ContactListProps> = ({ control, setValue, watch, errors }) => {
  const [state, setState] = useState<BaseContactListState>({
    contacts: [],
    newContact: { name: '', email: '', phone: '', extentionNo: '' },
    isAddingNew: false,
    isLoading: false,
    selectedContactId: '',
    isEditing: false,
    editingContact: null
  });

  const { contacts, newContact, isAddingNew, isLoading, selectedContactId, isEditing, editingContact } = state;

  const handleAddContact = (): void => {
    const { name, email, phone } = newContact;
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Invalid email format');
      return;
    }
    if (contacts.some(contact => contact.email === email)) {
      toast.error('Email already exists');
      return;
    }

    const updatedContacts = [...contacts, { ...newContact, _id: Date.now().toString() }];
    setState(prev => ({
      ...prev,
      contacts: updatedContacts,
      newContact: { name: '', email: '', phone: '', extentionNo: '' },
      isAddingNew: false
    }));
    setValue('contactPersons', updatedContacts);
  };

  const handleDeleteContact = (email: string): void => {
    const updatedContacts = contacts.filter(contact => contact.email !== email);
    setState(prev => ({ ...prev, contacts: updatedContacts }));
    setValue('contactPersons', updatedContacts);
    toast.success('Contact deleted successfully');
  };

  const handleEditContact = (contact: Contact): void => {
    setState(prev => ({
      ...prev,
      isEditing: true,
      editingContact: contact
    }));
  };

  const handleUpdateContact = (): void => {
    if (!editingContact) return;

    const { name, email, phone } = editingContact;
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Invalid email format');
      return;
    }

    const updatedContacts = contacts.map(contact =>
      contact._id === editingContact._id ? editingContact : contact
    );

    setState(prev => ({
      ...prev,
      contacts: updatedContacts,
      isEditing: false,
      editingContact: null
    }));
    setValue('contactPersons', updatedContacts);
    toast.success('Contact updated successfully');
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>, field: keyof Contact): void => {
    const value = e.target.value;
    if (isEditing && editingContact) {
      setState(prev => ({
        ...prev,
        editingContact: { ...editingContact, [field]: value }
      }));
    } else {
      setState(prev => ({
        ...prev,
        newContact: { ...prev.newContact, [field]: value }
      }));
    }
  };

  const toggleAddNew = (): void => {
    setState(prev => ({ ...prev, isAddingNew: !prev.isAddingNew }));
  };

  const cancelEdit = (): void => {
    setState(prev => ({
      ...prev,
      isEditing: false,
      editingContact: null
    }));
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0, gap:{xs:3, md:2} }}>
        <Box>
        <Typography fontSize={{xs:13, md:14}} fontWeight={600} textTransform={'capitalize'} mb={0.2}>Contact Persons</Typography>
        <Typography fontSize={{xs:13, md:14}} color='text.secondary' sx={{lineHeight:1.3}}>Add and manage primary contact information</Typography>
         </Box>

         <Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={toggleAddNew}
          disabled={isAddingNew || isEditing}
          sx={{
              borderRadius: {xs: "6px", md: "6px" },
              boxShadow:'none',
              py: 0.5,
              fontSize: '13px',
              pr: 1.5,
              pl: 1.5,
              '& .MuiButton-startIcon': {
                marginRight: '3px',
              },
            }}
        >
          Contact
        </Button>
        </Box>
      </Box>

      {contacts.length > 0 && (
        <TableContainer sx={{ mb:0, mt:1.5 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{fontSize:'13px'}}>Name</TableCell>
                <TableCell sx={{fontSize:'13px'}}>Email</TableCell>
                <TableCell sx={{fontSize:'13px'}}>Phone</TableCell>
                <TableCell sx={{fontSize:'13px'}}>Extension</TableCell>
                <TableCell sx={{fontSize:'13px'}}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {contacts.map((contact) => (
                <TableRow key={contact._id} sx={{ '&:last-child td': { border: 0 } }}>
                  <TableCell sx={{fontSize:'13.6px'}}>{contact.name}</TableCell>
                  <TableCell sx={{fontSize:'13.6px', textTransform:'lowercase'}}>{contact.email}</TableCell>
                  <TableCell sx={{fontSize:'13.6px'}}>{contact.phone}</TableCell>
                  <TableCell sx={{fontSize:'13.6px'}}>{contact.extentionNo || '-'}</TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleEditContact(contact)}
                      disabled={isEditing}
                    >
                      <EditIcon color='primary'sx={{fontSize:'15px'}}/>
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteContact(contact.email)}
                      disabled={isEditing}
                    >
                      <DeleteIcon color='error' sx={{fontSize:'15px'}}/>
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Modal Add New Contact */}

      {(isAddingNew || isEditing) && (
        <AppModalDialog open={!!(isAddingNew || isEditing)} onClose={isAddingNew ? toggleAddNew : cancelEdit}>
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            maxWidth: '90%',
            bgcolor: 'background.paper',
            boxShadow: 24,
            pt: 2,
            pl:{xs:2, md:3},
            pr:{xs:2, md:3},
            pb:1,
            borderRadius: 1.5
          }}>

            <Box sx={{display:'flex', alignItems:'center', justifyContent:'space-between', mb:2}}>
            <Typography variant="h6">
              {isAddingNew ? 'Add New Contact' : 'Edit Contact'}
            </Typography>

            <IconButton onClick={isAddingNew ? toggleAddNew : cancelEdit} size="small" sx={{ color: '#555' }}>
              {getIcon('CloseIcon')}
            </IconButton>
            </Box>

            <Grid container spacing={1.75}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Name"
                  size="small"
                  value={isAddingNew ? newContact.name : editingContact?.name || ''}
                  onChange={(e) => handleInputChange(e as ChangeEvent<HTMLInputElement>, 'name')}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  size="small"
                  value={isAddingNew ? newContact.email : editingContact?.email || ''}
                  onChange={(e) => handleInputChange(e as ChangeEvent<HTMLInputElement>, 'email')}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Phone"
                  size="small"
                  value={isAddingNew ? newContact.phone : editingContact?.phone || ''}
                  onChange={(e) => handleInputChange(e as ChangeEvent<HTMLInputElement>, 'phone')}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Extension"
                  size="small"
                  value={isAddingNew ? newContact.extentionNo : editingContact?.extentionNo || ''}
                  onChange={(e) => handleInputChange(e as ChangeEvent<HTMLInputElement>, 'extentionNo')}

                />
              </Grid>
            </Grid>

            <DialogActions sx={{ mt: 3, gap:0.5 }}>
              <Button variant='outlined' onClick={isAddingNew ? toggleAddNew : cancelEdit}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={isAddingNew ? handleAddContact : handleUpdateContact}
                disabled={isLoading}
              >
                {isAddingNew ? 'Add' : 'Update'}
              </Button>
            </DialogActions>
          </Box>
        </AppModalDialog>
      )}
    </Box>
  );
};

export default ContactList;
